import axios from 'axios';
import { getApiBaseUrl } from '../config/apiBase';

const client = axios.create({
  baseURL: '/server',
  withCredentials: true,
  timeout: 120000,
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
});

client.interceptors.request.use((config) => {
  config.baseURL = getApiBaseUrl();
  return config;
});

function bust() {
  return { _: Date.now() };
}

export async function fetchModelSections() {
  const { data } = await client.get('/list_models.php', { params: bust() });
  if (!data?.success) throw new Error(data?.error || 'Failed to list models');
  return data;
}

/** Cheap poll — detects renames/adds/deletes on disk without shipping the full tree. */
export async function fetchModelsFingerprint() {
  const { data } = await client.get('/list_models.php', {
    params: { fp_only: 1, ...bust() },
  });
  if (!data?.success) throw new Error(data?.error || 'Failed to read models fingerprint');
  return data;
}

export async function fetchLayerSections() {
  const { data } = await client.get('/list_layers.php', { params: bust() });
  if (!data?.success) throw new Error(data?.error || 'Failed to list layers');
  return data;
}

export async function uploadModel({ file, folder, displayName, replace = false, onProgress }) {
  const form = new FormData();
  form.append('file', file);
  if (folder) form.append('folder', folder.replace(/^models\//, ''));
  if (displayName) form.append('displayName', displayName);
  if (replace) form.append('replace', '1');
  const { data } = await client.post('/upload_model.php', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) {
        onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    },
  });
  if (data?.conflict) {
    const err = new Error(data.error || 'File already exists');
    err.conflict = true;
    err.path = data.path;
    err.name = data.name;
    throw err;
  }
  if (!data?.success) throw new Error(data?.error || 'Upload failed');
  return data;
}

export async function deleteModel(path) {
  const { data } = await client.post('/delete_model.php', { path });
  if (!data?.success) throw new Error(data?.error || 'Delete failed');
  return data;
}

export async function renameModel(from, to) {
  const { data } = await client.post('/rename_model.php', { from, to });
  if (data?.conflict) {
    const err = new Error(data.error || 'A file with that name already exists');
    err.conflict = true;
    throw err;
  }
  if (!data?.success) throw new Error(data?.error || 'Rename failed');
  return data;
}

export async function createFolder(parent, name) {
  const { data } = await client.post('/mkdir_model.php', { parent, name });
  if (data?.conflict) {
    const err = new Error(data.error || 'Folder already exists');
    err.conflict = true;
    throw err;
  }
  if (!data?.success) throw new Error(data?.error || 'Failed to create folder');
  return data;
}

export function dispatchModelsChanged(detail) {
  window.dispatchEvent(new CustomEvent('models-changed', { detail }));
}
